import { Component, inject, effect, input, output, OnInit, ChangeDetectionStrategy } from '@angular/core';
import Swal from 'sweetalert2';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ClaimOverviewService } from './claim-overview.service';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { LOCALE_ID } from '@angular/core';
import { FormMode } from 'app/core/constants/form-mode';

registerLocaleData(localeEs);

@Component({
    selector: 'app-claim-overview',
    templateUrl: './claim-overview.component.html',
    styleUrls: ['./claim-overview.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NgbModule,
        ReactiveFormsModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [ClaimOverviewService, { provide: LOCALE_ID, useValue: 'es-ES' }],
})
export class ClaimOverviewComponent implements OnInit {
    public Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
    });

    data = input.required<any>();
    onClose = output<boolean>();
    onUpdate = output<void>();

    public claimForm: FormGroup;
    public mode: FormMode = FormMode.VIEW;
    public formMode = FormMode;
    public tabSelected = 1;

    // Catálogos para select
    public claimTypes = [
        { code: 'accident', name: 'Accidente' },
        { code: 'theft', name: 'Robo' },
        { code: 'fire', name: 'Incendio' },
        { code: 'nature', name: 'Fenómeno Natural' },
        { code: 'malicious', name: 'Daño Malicioso' },
        { code: 'other', name: 'Otro' }
    ];

    public claimStatuses = [
        { code: 'reported', name: 'Reportado', class: 'bg-info' },
        { code: 'in_review', name: 'En Revisión', class: 'bg-warning' },
        { code: 'approved', name: 'Aprobado', class: 'bg-success' },
        { code: 'rejected', name: 'Rechazado', class: 'bg-danger' },
        { code: 'paid', name: 'Pagado', class: 'bg-success' }
    ];

    constructor(
        private formBuilder: FormBuilder,
        private claimOverviewService: ClaimOverviewService
    ) {
        // Reaccionar a cambios en el input data
        effect(() => {
            const claimData = this.data();
            if (claimData && this.claimForm) {
                this.claimForm.patchValue({
                    id: claimData.id,
                    vehicleId: claimData.vehicleId,
                    type: claimData.type,
                    description: claimData.description,
                    date: claimData.date,
                    status: claimData.status,
                    budgetAmount: claimData.budgetAmount,
                    deductibleAmount: claimData.deductibleAmount,
                    approvedAmount: claimData.approvedAmount
                });
            }
        });
    }

    ngOnInit(): void {
        this.createForm();
        if (this.data()) {
            const claimData = this.data();
            this.claimForm.patchValue({
                id: claimData.id,
                vehicleId: claimData.vehicleId,
                type: claimData.type,
                description: claimData.description,
                date: claimData.date,
                status: claimData.status,
                budgetAmount: claimData.budgetAmount,
                deductibleAmount: claimData.deductibleAmount,
                approvedAmount: claimData.approvedAmount
            });
        }
    }

    createForm() {
        this.claimForm = this.formBuilder.group({
            id: [null],
            vehicleId: [null, [Validators.required]],
            type: [null, [Validators.required]],
            description: [null],
            date: [null, [Validators.required]],
            status: ['reported', [Validators.required]],
            budgetAmount: [null],
            deductibleAmount: [null],
            approvedAmount: [null]
        });
    }

    submitClaim() {
        if (this.claimForm?.valid) {
            this.mode = this.formMode.VIEW;
            this.claimOverviewService.updateClaim(this.claimForm.value).subscribe({
                next: (res: any) => {
                    this.Toast.fire({
                        icon: 'success',
                        title: 'Siniestro actualizado correctamente',
                    });
                    this.onUpdate.emit();
                },
                error: (err) => {
                    console.error(err);
                    this.Toast.fire({
                        icon: 'error',
                        title: 'Error al actualizar siniestro'
                    });
                }
            });
        } else {
            console.warn('Formulario no inicializado o inválido');
        }
    }

    updateStatus(newStatus: string) {
        const statusName = this.claimStatuses.find(s => s.code === newStatus)?.name || newStatus;
        Swal.fire({
            icon: 'warning',
            title: 'Alerta',
            text: `¿Desea cambiar el estado a "${statusName}"?`,
            toast: false,
            position: 'center',
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: 'Aceptar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            timer: null,
            timerProgressBar: false
        }).then((result) => {
            if (result.isConfirmed) {
                this.claimOverviewService.updateClaimStatus(this.data().id, newStatus).subscribe({
                    next: (res: any) => {
                        this.data().status = newStatus;
                        this.claimForm.patchValue({ status: newStatus });
                        this.Toast.fire({
                            icon: 'success',
                            title: 'Estado actualizado correctamente',
                        });
                        this.onUpdate.emit();
                    },
                    error: (err) => {
                        console.error(err);
                        this.Toast.fire({
                            icon: 'error',
                            title: 'Error al actualizar estado'
                        });
                    }
                });
            }
        });
    }

    onModeChange(newMode: FormMode) {
        this.mode = newMode;
    }

    closeClaim() {
        this.onClose.emit(false);
    }

    getStatusClass(status: string): string {
        return this.claimStatuses.find(s => s.code === status)?.class || 'bg-secondary';
    }

    getStatusName(status: string): string {
        return this.claimStatuses.find(s => s.code === status)?.name || status;
    }

    getTypeName(type: string): string {
        return this.claimTypes.find(t => t.code === type)?.name || type;
    }
}
